import React, {useState} from 'react';

export interface FillPayload {
  title?: string;
  description?: string;
  startDate?: string;
  endDate?: string;
  city?: string;
  streetName?: string;
  streetNumber?: string;
  latitude?: string;
  longitude?: string;
  occurrences?: Array<{startDate: string; endDate: string}>;
}

interface FormFillerButtonProps {
  onFill: (payload: FillPayload) => void;
}

const SCHEMA_HINT = `// Basic event
${JSON.stringify(
  {
    title: 'My Event',
    description: 'A fun event',
    startDate: '2025-03-20T18:00:00',
    endDate: '2025-03-20T21:00:00',
    city: 'Bucuresti',
    streetName: 'Calea Victoriei',
    streetNumber: '10',
    latitude: '44.4268',
    longitude: '26.1025',
  },
  null,
  2,
)}

// Recurring event (2 occurrences)
${JSON.stringify(
  {
    title: 'Weekly Meetup',
    description: 'Happens every week',
    startDate: '2025-03-20T18:00:00',
    endDate: '2025-03-20T21:00:00',
    city: 'Bucuresti',
    streetName: 'Calea Victoriei',
    streetNumber: '10',
    latitude: '44.4268',
    longitude: '26.1025',
    occurrences: [
      {startDate: '2025-03-20T18:00:00', endDate: '2025-03-20T21:00:00'},
      {startDate: '2025-03-27T18:00:00', endDate: '2025-03-27T21:00:00'},
    ],
  },
  null,
  2,
)}`;

export const FormFillerButton: React.FC<FormFillerButtonProps> = ({onFill}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [jsonValue, setJsonValue] = useState('');
  const [message, setMessage] = useState('');
  const [isError, setIsError] = useState(false);
  const [isBtnHovered, setIsBtnHovered] = useState(false);
  const [isApplyHovered, setIsApplyHovered] = useState(false);
  const [isCloseHovered, setIsCloseHovered] = useState(false);

  const handleOpen = () => {
    setJsonValue('');
    setMessage('');
    setIsError(false);
    setIsOpen(true);
  };

  const handleClose = () => setIsOpen(false);

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) handleClose();
  };

  const handleApply = () => {
    setIsError(false);
    setMessage('');

    let data: FillPayload;
    try {
      data = JSON.parse(jsonValue.trim());
    } catch {
      setIsError(true);
      setMessage('✖ Invalid JSON — check your syntax.');
      return;
    }

    onFill(data);
    setMessage('✔ Applied!');
    setIsOpen(false);
  };

  const triggerBtnStyle: React.CSSProperties = {
    position: 'fixed',
    bottom: 28,
    right: 28,
    zIndex: 99999,
    background: '#daff00',
    color: '#000',
    border: 'none',
    borderRadius: 50,
    padding: '12px 22px',
    fontWeight: 700,
    fontSize: '0.9rem',
    cursor: 'pointer',
    boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
    transition: 'transform .15s, opacity .15s',
    transform: isBtnHovered ? 'scale(1.05)' : 'scale(1)',
    opacity: isBtnHovered ? 0.9 : 1,
  };

  const overlayStyle: React.CSSProperties = {
    display: isOpen ? 'flex' : 'none',
    position: 'fixed',
    inset: 0,
    zIndex: 100000,
    background: 'rgba(0,0,0,.7)',
    backdropFilter: 'blur(4px)',
    alignItems: 'center',
    justifyContent: 'center',
  };

  const modalStyle: React.CSSProperties = {
    background: '#2a2a2a',
    border: '1px solid #444',
    borderRadius: 16,
    padding: '28px 28px 24px',
    width: 'min(520px, 94vw)',
    boxShadow: '0 20px 60px rgba(0,0,0,.6)',
  };

  const schemaCodeStyle: React.CSSProperties = {
    display: 'block',
    background: '#1a1a1a',
    borderRadius: 8,
    padding: '10px 12px',
    color: '#daff00',
    fontSize: '0.72rem',
    marginBottom: 14,
    whiteSpace: 'pre',
    overflowX: 'auto',
  };

  const textareaStyle: React.CSSProperties = {
    width: '100%',
    height: 180,
    background: '#1a1a1a',
    border: '1px solid #444',
    borderRadius: 8,
    color: '#fff',
    fontSize: '0.82rem',
    padding: 12,
    boxSizing: 'border-box',
    resize: 'vertical',
    outline: 'none',
    fontFamily: 'monospace',
  };

  const applyBtnStyle: React.CSSProperties = {
    flex: 1,
    background: '#daff00',
    color: '#000',
    border: 'none',
    borderRadius: 8,
    padding: 11,
    fontWeight: 700,
    fontSize: '0.88rem',
    cursor: 'pointer',
    opacity: isApplyHovered ? 0.85 : 1,
  };

  const closeBtnStyle: React.CSSProperties = {
    background: 'transparent',
    color: isCloseHovered ? '#fff' : '#aaa',
    border: `1px solid ${isCloseHovered ? '#888' : '#555'}`,
    borderRadius: 8,
    padding: '11px 18px',
    fontSize: '0.88rem',
    cursor: 'pointer',
  };

  const messageStyle: React.CSSProperties = {
    marginTop: 10,
    fontSize: '0.78rem',
    minHeight: 18,
    color: isError ? '#ff5555' : '#4cff72',
    whiteSpace: 'pre-wrap',
  };

  return (
    <>
      <button
        style={triggerBtnStyle}
        onMouseEnter={() => setIsBtnHovered(true)}
        onMouseLeave={() => setIsBtnHovered(false)}
        onClick={handleOpen}>
        ⚡ Fill Form
      </button>

      <div style={overlayStyle} onClick={handleOverlayClick}>
        <div style={modalStyle}>
          <h3 style={{color: '#fff', margin: '0 0 6px', fontSize: '1.1rem'}}>
            📋 Form Filler
          </h3>
          <p
            style={{
              color: '#888',
              fontSize: '0.78rem',
              margin: '0 0 14px',
              lineHeight: 1.5,
            }}>
            Paste JSON below. Dates must be ISO: "2025-03-20T18:00:00". Omit
            any key to leave it untouched.
          </p>
          <code style={schemaCodeStyle}>{SCHEMA_HINT}</code>
          <textarea
            style={textareaStyle}
            placeholder="Paste JSON here…"
            value={jsonValue}
            onChange={e => setJsonValue(e.target.value)}
          />
          <div style={{display: 'flex', gap: 10, marginTop: 14}}>
            <button
              style={applyBtnStyle}
              onMouseEnter={() => setIsApplyHovered(true)}
              onMouseLeave={() => setIsApplyHovered(false)}
              onClick={handleApply}>
              Apply
            </button>
            <button
              style={closeBtnStyle}
              onMouseEnter={() => setIsCloseHovered(true)}
              onMouseLeave={() => setIsCloseHovered(false)}
              onClick={handleClose}>
              Cancel
            </button>
          </div>
          {message && <div style={messageStyle}>{message}</div>}
        </div>
      </div>
    </>
  );
};
